import { type UserActivityEntry } from '../../models/ActivityObject';
import { isToday, isYesterday } from '../../utils/date';

import { messages } from './messages';

export const getRelativeTime = (activityDate: Date) => {
	if (isYesterday(activityDate)) {
		return 'Yesterday';
	}
	if (isToday(activityDate)) {
		return 'Today';
	}

	return 'Time';
};

export enum ActivityType {
	EDITED = 'Edited',
	COMMENTED = 'Commented',
	CREATED = 'Created',
	WORKED = 'Worked',
	// some new event type from Worklens
	ASSIGNED = 'Assigned',
	UNASSIGNED = 'Unassigned',
	LIKED = 'Liked',
	PUBLISHED = 'Published',
	UPDATED = 'Updated',
	// are not used yet.
	// TRANSITIONED = 'Transitioned',
	// VIEWED = 'Viewed'
}

export const getUserActivityType = (activityType: string): ActivityType => {
	const allActivityVerbs = Object.values(ActivityType);
	const supportedVerb = allActivityVerbs.find((actionType: string) => {
		return actionType.toLowerCase() === activityType.toLowerCase();
	});
	return supportedVerb || ActivityType.WORKED;
};

export const getI18nDataForSingleEntry = (
	activityEntry: UserActivityEntry,
	currentUserId: string,
) => {
	// Due to i18n'ing being a hard problem, there are many possible cases already listed in messages.
	// We'll construct the key rather than list them all here.
	// Key will be of the form: [you|other]Individual[Verb][Today|Yesterday|Time]
	const whoMadeActivity =
		activityEntry.user && activityEntry.user.id === currentUserId ? 'you' : 'other';
	const activityVerb = getUserActivityType(activityEntry.type);
	const activityTime = getRelativeTime(
		activityEntry.date ? new Date(activityEntry.date) : new Date(),
	);

	const params: {
		name: string;
		time?: string | JSX.Element;
	} = {
		name: activityEntry.user.fullName || '',
	};
	if (activityTime === 'Time') {
		params.time = activityEntry.date || undefined;
	}

	return {
		key: messages[
			(whoMadeActivity + 'Individual' + activityVerb + activityTime) as keyof typeof messages
		],
		params,
	};
};

const I18N_KEYS_VARIATIONS_FOR_MULTIPLE = {
	you: {
		// including "with you" meaning for more than 1 activity
		'2': messages.youDoubleWorkedOn,
		'3': messages.youTripleWorkedOn,
		many: messages.youManyWorkedOn,
	},
	others: {
		'2': messages.othersDoubleWorkedOn,
		'3': messages.othersTripleWorkedOn,
		many: messages.othersManyWorkedOn,
	},
};

// Will return something like "Bob and John worked on this"
export const getI18nDataForManyEntries = (
	userActivityEntries: UserActivityEntry[],
	currentUser: string,
) => {
	const containsCurrentUser = userActivityEntries.some(
		(activity) => activity.user.id === currentUser,
	);

	const otherUserFullNames = userActivityEntries
		.filter((activity) => activity.user.id !== currentUser)
		.map((activity) => activity.user.fullName);

	// params that we will pass to our translations
	const othersCount = otherUserFullNames.length - (containsCurrentUser ? 1 : 2);
	const names = {};
	otherUserFullNames.forEach((userFullName, idx) => {
		// @ts-ignore
		names[`name${idx + 1}`] = userFullName;
	});

	const activitiesCount = userActivityEntries.length;
	const collabAmount = activitiesCount <= 3 ? activitiesCount : 'many';
	const collabType = containsCurrentUser ? 'you' : 'others';

	return {
		// @ts-ignore
		key: I18N_KEYS_VARIATIONS_FOR_MULTIPLE[collabType][collabAmount],
		params: {
			count: othersCount,
			...names,
		},
	};
};

/**
 * https://product-fabric.atlassian.net/browse/FIND-202
 * Since Worklens API does not return a status to indicate status of Confluence page,
 * this is workaround to detect an work item as draft or not.
 */
export function isConfluenceDraft(provider: string, objectUrl: string): boolean {
	if (!provider || !objectUrl) {
		return false;
	}

	return (
		provider.toLowerCase() === 'confluence' &&
		(objectUrl.includes('draftId=') || objectUrl.includes('draftShareId='))
	);
}
